<script lang="ts">
  import type {
    MatchStakeholdersRequestFields,
    MatchStakeholdersRequestResponse,
    MatchStakeholdersRequestParams
  } from '@gat-ship/types'

  export interface DuplicateStakeholderProviderContext {
    fieldCandidates: Ref<
      Partial<
        Record<
          keyof MatchStakeholdersRequestFields,
          MatchStakeholdersRequestResponse['candidates']
        >
      >
    >
    allCandidates: Ref<MatchStakeholdersRequestResponse['candidates']>
    checkForDuplicateStakeholders: (
      params: CheckForDuplicateStakeholdersParams
    ) => Promise<MatchStakeholdersRequestResponse>
  }

  export type CheckForDuplicateStakeholdersParams = {
    field: keyof MatchStakeholdersRequestFields
    value: MatchStakeholdersRequestFields[keyof MatchStakeholdersRequestFields]
    options?: {
      /** Whether to update the candidates list in the provider context. @default true */
      updateCandidates?: boolean
    }
  }
  // No external consumer of `useDuplicateStakeholderProvider` exists today —
  // skip the first destructure slot so we don't ship a public-API symbol no
  // one calls. The provide half stays module-local and is used by the setup
  // block below.
  const [, provideDuplicateStakeholder] =
    createContext<DuplicateStakeholderProviderContext>(
      'duplicate-stakeholder-provider'
    )
</script>

<script setup lang="ts">
  import { createContext } from 'reka-ui'
  import { ref, type Ref } from 'vue'
  import { useStakeholderApiCalls } from '../../composables/useStakeholderApiCalls'

  const { matchStakeholdersRequest } = useStakeholderApiCalls()

  const allCandidates = ref<MatchStakeholdersRequestResponse['candidates']>([])

  const fieldCandidates = ref<
    Partial<
      Record<
        keyof MatchStakeholdersRequestFields,
        MatchStakeholdersRequestResponse['candidates']
      >
    >
  >({})

  const fields = ref<Partial<MatchStakeholdersRequestFields>>({})

  async function checkForDuplicateStakeholders(
    params: CheckForDuplicateStakeholdersParams
  ) {
    if (!params.value) {
      delete fields.value?.[params.field]
      return {
        candidates: [],
        limit: 0,
        returned: 0
      }
    }
    fields.value = { ...fields.value, [params.field]: params.value }
    const requestParams: MatchStakeholdersRequestParams = {
      type: 'client',
      name_matching: 'exact',
      ...fields.value
    }
    const response = await matchStakeholdersRequest(requestParams)
    if (params.options?.updateCandidates !== false) {
      allCandidates.value = response.candidates
      fieldCandidates.value = {
        ...fieldCandidates.value,
        [params.field]: response.candidates
      }
    }
    return response
  }

  provideDuplicateStakeholder({
    fieldCandidates,
    allCandidates,
    checkForDuplicateStakeholders
  })
</script>
<template>
  <slot />
</template>
